import { type FormikErrors } from 'formik';
import type {
  BatchReceiptDetails,
  ReceiptBatchFormValues,
} from './ServiceProviderReceiptBatchForm';
import React, { memo, useEffect, useMemo } from 'react';
import { SelectAllResidentsView } from '@pflegenavi/frontend/resident-components';
import { useTranslation } from 'react-i18next';
import type { Gender } from '@pflegenavi/shared/api';
import { useReceiptBatchStore } from '../ReceiptBatchContext';
import { useShallow } from 'zustand/react/shallow';

interface ServiceProviderReceiptBatchSelectResidentProps {
  index: number;
  receipt: BatchReceiptDetails;
  setReceiptFieldValue: (field: string, value: any) => void;
  residents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  archivedResidents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  isLoadingResidents: boolean;
  enqueueSnackbarOnce: (message: string, options?: any) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const ServiceProviderReceiptBatchSelectResident: React.FC<ServiceProviderReceiptBatchSelectResidentProps> =
  memo(
    ({
      receipt,
      index,
      setReceiptFieldValue,
      residents,
      archivedResidents,
      isLoadingResidents,
      enqueueSnackbarOnce,
      onKeyUp,
    }) => {
      const { t } = useTranslation();

      const [
        errors,
        touched,
        residentId,
        setFieldTouched,
        individualDatesMode,
      ] = useReceiptBatchStore(
        useShallow((state) => [
          state.formik?.errors?.receipts?.[index] as FormikErrors<
            ReceiptBatchFormValues['receipts'][number]
          >,
          state.formik?.touched?.receipts?.[index]?.residentId,
          state.formik?.values?.receipts?.[index]?.residentId,
          state.formik?.setFieldTouched,
          state.formik?.values.individualDatesMode,
        ])
      );

      const selectedResident = useMemo(() => {
        return (
          residents?.find((resident) => resident.id === receipt.residentId) ??
          archivedResidents?.find(
            (resident) => resident.id === receipt.residentId
          )
        );
      }, [receipt.residentId, residents, archivedResidents]);

      const showError = useMemo(() => {
        return (
          Boolean(errors?.residentId) &&
          (residentId !== undefined || touched !== undefined)
        );
      }, [errors?.residentId, residentId, touched]);

      useEffect(() => {
        if (
          !isLoadingResidents &&
          receipt.residentId !== undefined &&
          selectedResident === undefined
        ) {
          setReceiptFieldValue('residentId', undefined);
          enqueueSnackbarOnce(t('receipts.batch.form.resident-not-found'), {
            variant: 'error',
          });
        }
      }, [
        enqueueSnackbarOnce,
        index,
        isLoadingResidents,
        receipt.residentId,
        selectedResident,
        setReceiptFieldValue,
        t,
      ]);

      if (isLoadingResidents) {
        return null;
      }

      return (
        <SelectAllResidentsView
          name={`receipts.${index}.residentId`}
          residents={residents}
          selectedResident={selectedResident}
          setResidentId={(residentId) => {
            setReceiptFieldValue('residentId', residentId);
          }}
          error={showError}
          helperText={showError ? errors?.residentId : undefined}
          autoFocus={index > 0}
          handleBlur={() => {
            setFieldTouched?.(`receipts.${index}.residentId`);
          }}
          variant="standard"
          hideAvatar
          InputLabelProps={{ shrink: true }}
          label={t('receipts.batch.form.resident')}
          onKeyUp={onKeyUp}
          maxWidth={individualDatesMode ? 230 : undefined}
          minWidth={individualDatesMode ? 150 : undefined}
        />
      );
    }
  );
